import { clsx } from "clsx";

import { Container } from "~/components/container";
import { FadeIn, FadeInStagger } from "~/components/fade-in";
import { processSteps } from "~/lib/site";

/**
 * The delivery schedule. Each stage is marked by an oversized tonal numeral
 * hung out to the container's edge — the sequence is the most physical thing
 * on the page, because the sequence is what we are actually selling.
 *
 * The hang starts at `sm`: on a phone the container's gutter is the screen
 * edge, so hanging there puts the numeral flush against it with none of the
 * breathing room every other element gets, and it reads as clipped.
 */
export function Delivery({ className }: { className?: string }) {
  return (
    <Container id="delivery" className={clsx("scroll-mt-12", className)}>
      <FadeIn>
        <p className="label text-accent-400">How we deliver</p>
        <h2 className="mt-5 max-w-2xl font-display text-4xl font-medium tracking-tight text-ink-50 [text-wrap:balance] sm:text-5xl">
          Six stages from first call to store listing.
        </h2>
        <p className="mt-5 max-w-2xl text-lg text-ink-300">
          Every engagement runs the same rhythm. You always know what is being
          built, what it costs, and when you can install it.
        </p>
      </FadeIn>

      <FadeInStagger>
        <ol className="mt-14 border-t border-ink-700">
          {processSteps.map((step, index) => (
            <li key={step.title}>
              <FadeIn className="flex items-center gap-6 border-b border-ink-700 py-8 sm:gap-10 sm:py-10">
                <div className="min-w-0 flex-auto">
                  <p className="label text-ink-400">{step.when}</p>
                  <h3 className="mt-3 font-display text-xl font-semibold tracking-tight text-ink-50 sm:text-2xl">
                    {step.title}
                  </h3>
                  <p className="mt-3 max-w-xl text-base text-ink-300">
                    {step.description}
                  </p>
                </div>
                <span
                  aria-hidden="true"
                  className="flex-none select-none font-display text-6xl font-bold leading-none tracking-tighter text-ink-700 [font-variant-numeric:tabular-nums] sm:-mr-6 sm:text-8xl lg:-mr-8 lg:text-[8.5rem]"
                >
                  {String(index + 1).padStart(2, "0")}
                </span>
              </FadeIn>
            </li>
          ))}
        </ol>
      </FadeInStagger>
    </Container>
  );
}
